'use client';

import type { Dispatch, SetStateAction } from 'react';
import type { TFunction } from 'i18next';
import type { ColumnDef, RowSelectionState, VisibilityState } from '@tanstack/react-table';
import { Loader2 } from 'lucide-react';
import type { CompanySearchResult } from '@/lib/actions/company-search';
import type { useLocale } from '@/hooks/use-locale';
import { TableImplementation } from './ResultsTableParts';
import { MobileResultsList } from './MobileResultsList';

interface ResultsTableBodyProps {
  t: TFunction<'translation', undefined>;
  isLoading: boolean;
  previewEuId: string | null | undefined;
  onOpenPreview: ((euId: string, trigger?: HTMLElement) => void) | undefined;
  displayedResults: CompanySearchResult[];
  mobileResults: CompanySearchResult[];
  columns: ColumnDef<CompanySearchResult>[];
  columnVisibility: VisibilityState;
  handleColumnVisibilityChange: (id: string, visible: boolean) => void;
  total: number;
  totalLabel: string;
  locale: ReturnType<typeof useLocale>['locale'];
  offset: number;
  limit: number;
  onOffsetChange: ((offset: number) => void) | undefined;
  onLimitChange: ((limit: number) => void) | undefined;
  rowSelection: RowSelectionState;
  setRowSelection: Dispatch<SetStateAction<RowSelectionState>>;
}

/** The results area: a spinner while loading, else the desktop grid and the mobile card list. */
export function ResultsTableBody({
  t,
  isLoading,
  previewEuId,
  onOpenPreview,
  displayedResults,
  mobileResults,
  columns,
  columnVisibility,
  handleColumnVisibilityChange,
  total,
  totalLabel,
  locale,
  offset,
  limit,
  onOffsetChange,
  onLimitChange,
  rowSelection,
  setRowSelection,
}: ResultsTableBodyProps) {
  return isLoading ? (
    <div className="flex items-center justify-center h-full">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
    </div>
  ) : (
    <>
      {/* Desktop: rich DataGrid table */}
      <div className="hidden lg:block h-full">
        <TableImplementation
          previewEuId={previewEuId}
          onOpenPreview={onOpenPreview}
          data={displayedResults}
          columns={columns}
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          total={total}
          totalLabel={totalLabel}
          locale={locale}
          offset={offset}
          limit={limit}
          onOffsetChange={onOffsetChange}
          onLimitChange={onLimitChange}
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
        />
      </div>
      {/* Mobile: list of cards. The page itself scrolls (body overflow
          is only locked on desktop), so this branch flows naturally —
          no inner scroll container with a height it can't compute.
          Cards include the first line of the AI summary when available;
          pagination sits below the list. */}
      <MobileResultsList
        mobileResults={mobileResults}
        t={t}
        onOffsetChange={onOffsetChange}
        total={total}
        offset={offset}
        totalLabel={totalLabel}
      />
    </>
  );
}
